import { startCase } from 'lodash';
import { useMemo } from 'react';

import {
  type Item,
  SelectionCategories,
  type SelectionList,
  type SelectionListIdNames,
} from '@revelio/iso-utility';

import { useSelectionListsForKeys } from '../../../../shared/selection-lists/hooks/useSelectionListsForKeys';
import { SelectionCategoryLabelOverrides } from '../../../../shared/selection-lists/selection-category-label-overrides';
import type { GqlSelectionListKey } from '../../../../shared/selection-lists/types';
import { useRoleSelectionLists } from '../../api/useRoleSelectionLists';
import { useSkillSelectionLists } from '../../api/useSkillSelectionLists';
import {
  selectModelVersions,
  useDataBuilderSelector,
} from '../../data-builder.store';
import { isV3Taxonomy } from '../../utils/job-taxonomy-version-migration';
import {
  addParentFieldAliases,
  getRoleHierarchy,
  getSkillHierarchy,
  resolveHierarchyKeys,
  toApiKey,
  validateHierarchy,
} from './data-builder-selection-list-mapping';

export type UseDataBuilderSelectionListsOptions = {
  apiKeys: string[];
};

export function useDataBuilderSelectionLists({
  apiKeys,
}: UseDataBuilderSelectionListsOptions) {
  const modelVersions = useDataBuilderSelector(selectModelVersions);
  const jobTaxonomyVersion = modelVersions.job_taxonomy_version?.toString();
  const skillTaxonomyVersion = modelVersions.skill_taxonomy_version?.toString();
  const jobTaxonomyId = modelVersions.job_taxonomy_id;
  const skillTaxonomyId = modelVersions.skill_taxonomy_id;
  const isV3Job = isV3Taxonomy(jobTaxonomyVersion);
  const isV3Skill = isV3Taxonomy(skillTaxonomyVersion);
  const resolved = useMemo(
    () => resolveHierarchyKeys(apiKeys, isV3Job, isV3Skill),
    [apiKeys, isV3Job, isV3Skill]
  );

  const roleFetchKeys = useMemo(
    () => resolved.role.map(String),
    [resolved.role]
  );

  const { roleSelectionLists } = useRoleSelectionLists(roleFetchKeys, {
    jobTaxonomyVersion,
    taxonomyId: jobTaxonomyId as number | undefined,
  });

  const skillFetchKeys = useMemo(
    () => resolved.skill.map(String),
    [resolved.skill]
  );

  // Skill (both V2 and V3) is resolved here, not via the shared router:
  // V3 from the skill taxonomy tree, V2 from the legacy skill query — the same
  // split useRoleSelectionLists uses for roles.
  const { skillSelectionLists } = useSkillSelectionLists(skillFetchKeys, {
    skillTaxonomyVersion,
    taxonomyId: skillTaxonomyId as number | undefined,
  });

  // RICS → taxonomyTree, geo → taxonomyTree, demographics → referenceLists.
  const gqlFetchKeys = useMemo(
    () => resolved.gql as GqlSelectionListKey[],
    [resolved.gql]
  );

  const { selectionLists: nonRoleSelectionListState } =
    useSelectionListsForKeys(gqlFetchKeys);

  const selectionLists = useMemo(() => {
    const byId = new Map<string, SelectionList>();

    // nonRoleSelectionListState is Partial<Record<GqlSelectionListKey, unknown[]>>
    // — cast each list to Item[] at the boundary so downstream normalization works.
    if (nonRoleSelectionListState) {
      for (const key of gqlFetchKeys) {
        const list = nonRoleSelectionListState[key] as Item[] | undefined;
        if (list) {
          const normalizedValue = (list[0] as unknown as SelectionList)?.label
            ? list
            : list.map((item) => ({
                ...item,
                label: item.longName,
              }));

          byId.set(String(key), {
            id: key,
            label: key,
            value: addParentFieldAliases(normalizedValue as Item[]),
          } as SelectionList);
        }
      }
    }

    if (roleSelectionLists) {
      Object.values(roleSelectionLists).forEach((list) => {
        if (list) byId.set(String(list.id), list as SelectionList);
      });
    }

    if (skillSelectionLists) {
      Object.values(skillSelectionLists).forEach((list) => {
        if (list) byId.set(String(list.id), list);
      });
    }

    return resolved.all
      .map((key) => byId.get(String(key)))
      .filter((l): l is SelectionList => !!l);
  }, [
    resolved.all,
    gqlFetchKeys,
    nonRoleSelectionListState,
    roleSelectionLists,
    skillSelectionLists,
  ]);

  const branchLabels = useMemo(
    () =>
      selectionLists.map((l) => {
        // The top role level's label tracks the taxonomy version like its API
        // key does (V2: k7, V3: k10); the shared override map only knows the V3
        // form, so resolve it here before falling back to the map.
        if (String(l.id) === SelectionCategories.JOB_CATEGORY) {
          return isV3Job ? 'k10' : 'k7';
        }
        return (
          SelectionCategoryLabelOverrides[String(l.id)] ??
          startCase(toApiKey(l.id as SelectionListIdNames, isV3Job))
        );
      }),
    [selectionLists, isV3Job]
  );

  if (process.env.NODE_ENV === 'development') {
    if (resolved.role.length > 0) {
      const roleWarning = validateHierarchy(
        resolved.role,
        getRoleHierarchy(isV3Job)
      );
      if (roleWarning) console.warn(roleWarning);
    }
    if (resolved.skill.length > 0) {
      const skillWarning = validateHierarchy(
        resolved.skill,
        getSkillHierarchy(isV3Skill)
      );
      if (skillWarning) console.warn(skillWarning);
    }
  }

  return { selectionLists, branchLabels, resolvedKeys: resolved.all, isV3Job };
}
